/**
 * @file ClientsPerTier.js
 * @description Card for the Clients Per Tier dashboard.
 */

import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { DASHBOARD_AS_OF_DATE_FORMAT } from './DashboardUtils';
import { tenantTierLabels } from '../../utils/data/enums';
import NotEnoughData from './NotEnoughData';
import './ClientsPerTier.scss';
import { getText } from '../../i18n';

const TIER_DISPLAY_ORDER = [4, 3, 2, 1];

function mapApiResponseToTierData(dataFromApi = []) {
    const safeData = Array.isArray(dataFromApi) ? dataFromApi : [];
    const countsByTier = {
        1: 0,
        2: 0,
        3: 0,
        4: 0,
    };

    safeData.forEach(({ tier, count }) => {
        if (!(tier in countsByTier)) {
            return;
        }
        countsByTier[tier] += Number(count) || 0;
    });

    return TIER_DISPLAY_ORDER.map(tierId => ({
        id: tierId,
        name: tenantTierLabels[tierId] || 'Unknown',
        value: countsByTier[tierId],
    }));
}

function isTierDataEmpty(data) {
    if (!Array.isArray(data) || data.length === 0) {
        return true;
    }

    return data.every(item => item.value === 0);
}

function ClientsPerTier({ clientTierData, endDate }) {
    const [tierData, setTierData] = useState(() =>
        mapApiResponseToTierData(clientTierData),
    );
    const formattedAsOfDate = format(
        new Date(endDate),
        DASHBOARD_AS_OF_DATE_FORMAT,
    );
    const total = tierData.reduce((sum, item) => sum + item.value, 0);

    useEffect(() => {
        setTierData(mapApiResponseToTierData(clientTierData));
    }, [clientTierData]);

    if (isTierDataEmpty(tierData)) {
        return <NotEnoughData />;
    }

    return (
        <div className="clients-per-tier">
            <div className="clients-per-tier-date-subtitle">
                {getText('msg_a8770f2d4d18')} {formattedAsOfDate}
            </div>
            <div className="clients-per-tier-list">
                {tierData.map(tier => {
                    const percentage = total
                        ? ((tier.value / total) * 100).toFixed(1)
                        : '0.0';

                    return (
                        <div key={tier.id} className="clients-per-tier-row">
                            <div className="clients-per-tier-row-left">
                                <span
                                    className={`clients-per-tier-dot tier-${tier.id}`}
                                />
                                <span className="clients-per-tier-label">
                                    {tier.name}
                                </span>
                            </div>
                            <div className="clients-per-tier-value">
                                {tier.value.toLocaleString()} ({percentage}%)
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ClientsPerTier;
